import React, { useCallback, useEffect, useState } from 'react';
import { Icon, ChannelBadge, channelLabel } from './inboxIcons';

const CH_COLOR = { email: 'var(--em)', form: 'var(--form)', ig: 'var(--ig)', fb: 'var(--fb)', chat: 'var(--chat)', manual: 'var(--chat)', wa: 'var(--wa)' };
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

function dur(min) {
  if (min == null) return '—';
  if (min < 60) return `${Math.round(min)} min`;
  if (min < 48 * 60) return `${Math.round((min / 60) * 10) / 10} h`;
  return `${Math.round((min / 1440) * 10) / 10} days`;
}

// Change against the previous period. For times, lower is better.
function Delta({ now, prev, lowerBetter, asPct }) {
  if (now == null || prev == null || prev === 0) return null;
  const diff = asPct ? (now - prev) * 100 : ((now - prev) / prev) * 100;
  if (Math.abs(diff) < 1) return <span className="cx-delta flat">no change</span>;
  const good = lowerBetter ? diff < 0 : diff > 0;
  return <span className={`cx-delta ${good ? 'up' : 'down'}`}>{diff > 0 ? '+' : '−'}{Math.round(Math.abs(diff))}{asPct ? ' pts' : '%'}</span>;
}

// embedded: shown inside Analytics (its own header and period picker are hidden; the page's period is used)
function ReportsPage({ api, embedded = false, days: daysProp }) {
  const [ownDays, setDays] = useState(7);
  const days = embedded ? daysProp || 30 : ownDays;
  const [report, setReport] = useState(null);
  const [live, setLive] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, m] = await Promise.all([api(`/api/v1/support/reports?days=${days}`), api('/api/v1/support/metrics').catch(() => null)]);
      setReport(r.report);
      setLive(m);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, days]);

  useEffect(() => { load(); }, [load]);

  const r = report;
  const maxDay = r ? Math.max(1, ...r.perDay.map((d) => d.count)) : 1;
  const chTotal = r ? Object.values(r.byChannel).reduce((s, n) => s + n, 0) : 0;
  const channels = r ? Object.entries(r.byChannel).sort((a, b) => b[1] - a[1]) : [];
  const dayLabel = (k) => new Date(`${k}T00:00:00+05:30`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

  const body = (
    <>
        {!embedded && <div className="cx-page-head">
          <div>
            <h1>Reports</h1>
            <p className="cx-lede">How quickly customers hear back, what they write about, and how the AI agent is doing. Compared with the {days} days before.</p>
          </div>
          <div className="cx-page-actions">
            <div className="cx-seg" role="group" aria-label="Period" style={{ minWidth: 240 }}>
              {[7, 30, 90].map((d) => <button key={d} aria-pressed={days === d} onClick={() => setDays(d)}>{d} days</button>)}
            </div>
            <button className="cx-btn cx-btn-icon" onClick={load} disabled={loading} aria-label="Refresh"><Icon name="refresh" size={16} /></button>
          </div>
        </div>}

        {live && (
          <div className="cx-panel" style={{ marginBottom: 20 }}>
            <div className="cx-panel-h"><h2>Right now</h2><span>tickets and cases</span></div>
            <div className="cx-panel-b" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span className="cx-chip cx-c-new" style={{ fontSize: 13, lineHeight: '26px', padding: '0 10px' }}>New <b className="cx-mono">{live.newCount ?? 0}</b></span>
              <span className="cx-chip cx-c-open" style={{ fontSize: 13, lineHeight: '26px', padding: '0 10px' }}>In progress <b className="cx-mono">{live.openCount ?? 0}</b></span>
              <span className="cx-chip cx-c-waiting" style={{ fontSize: 13, lineHeight: '26px', padding: '0 10px' }}>Waiting on customer <b className="cx-mono">{live.pendingCount ?? 0}</b></span>
              <span className="cx-chip cx-c-resolved" style={{ fontSize: 13, lineHeight: '26px', padding: '0 10px' }}>Resolved today <b className="cx-mono">{live.resolvedTodayCount ?? 0}</b></span>
              {live.overdueCount > 0 && <span className="cx-chip cx-c-time hot" style={{ fontSize: 13, lineHeight: '26px', padding: '0 10px' }}>Overdue <b className="cx-mono">{live.overdueCount}</b></span>}
            </div>
          </div>
        )}

        {error && !r ? (
          <div className="cx-empty"><strong>Couldn't load reports</strong>{error}<div style={{ marginTop: 12 }}><button className="cx-btn" onClick={load}>Try again</button></div></div>
        ) : !r ? (
          <div aria-busy="true"><div className="cx-skel" style={{ height: 96, margin: 0 }} /><div className="cx-skel" style={{ height: 220, margin: '20px 0 0' }} /></div>
        ) : (
          <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
            <div className="cx-metrics">
              <div className="cx-metric"><span>Customers who wrote</span><b>{r.active.now}</b><small><Delta now={r.active.now} prev={r.active.prev} /> {r.conversations.now} new conversations</small></div>
              <div className="cx-metric">
                <span>Median first reply</span><b>{dur(r.firstReplyMin.now)}</b>
                <small>{r.firstReplyMin.samples ? <><Delta now={r.firstReplyMin.now} prev={r.firstReplyMin.prev} lowerBetter /> {r.firstReplyMin.samples} conversations</> : 'tracked from 8 Oct 2026'}</small>
              </div>
              <div className="cx-metric">
                <span>Median time to resolve</span><b>{dur(r.resolutionMin.now)}</b>
                <small><Delta now={r.resolutionMin.now} prev={r.resolutionMin.prev} lowerBetter /> {r.resolved.now} resolved in period</small>
              </div>
              <div className="cx-metric">
                <span>AI drafts sent as written</span><b>{r.drafts.asIsPct == null ? '—' : `${Math.round(r.drafts.asIsPct * 1000) / 10}%`}</b>
                <small><Delta now={r.drafts.asIsPct} prev={r.drafts.asIsPctPrev} asPct /> {r.drafts.decided} reviewed · {r.drafts.edited} edited · {r.drafts.rejected} rejected</small>
              </div>
            </div>

            <div className="cx-two">
              <section className="cx-panel" aria-labelledby="cx-vol-h">
                <div className="cx-panel-h"><h2 id="cx-vol-h">New conversations per day</h2><span>{r.conversations.now} total</span></div>
                <div className="cx-vol" role="img" aria-label={`New conversations per day, highest ${maxDay}`}>
                  {r.perDay.map((d) => (
                    <div key={d.day} title={`${dayLabel(d.day)}: ${d.count}`}>
                      <em>{d.count}</em>
                      <i style={{ height: `${(d.count / maxDay) * 100}%` }} />
                    </div>
                  ))}
                </div>
                <div className="cx-vol-axis"><span>{dayLabel(r.perDay[0].day)}</span><span>{dayLabel(r.perDay[r.perDay.length - 1].day)}</span></div>
              </section>
              <section className="cx-panel" aria-labelledby="cx-ch-h">
                <div className="cx-panel-h"><h2 id="cx-ch-h">By channel</h2><span>new conversations</span></div>
                <div className="cx-panel-b">
                  {chTotal === 0 ? <span className="cx-hint">No new conversations in this period.</span> : (
                    <>
                      <div className="cx-split">{channels.map(([ch, n]) => <i key={ch} style={{ width: `${(n / chTotal) * 100}%`, background: CH_COLOR[ch] || 'var(--em)' }} title={`${channelLabel(ch)} ${n}`} />)}</div>
                      <div className="cx-legend">
                        {channels.map(([ch, n]) => <div key={ch}><ChannelBadge channel={ch} />{channelLabel(ch)}<b>{n}</b><small>{Math.round((n / chTotal) * 100)}%</small></div>)}
                      </div>
                    </>
                  )}
                </div>
              </section>
            </div>

            <div className="cx-two even">
              <section className="cx-panel" aria-labelledby="cx-top-h">
                <div className="cx-panel-h"><h2 id="cx-top-h">What customers write about</h2><span>AI-sorted topics</span></div>
                {r.topics.length === 0 ? <div className="cx-empty" style={{ padding: 28 }}><strong>No topics yet</strong>Topics appear once the AI agent has read new messages.</div> : (
                  <div style={{ paddingTop: 8 }}>
                    {r.topics.map((t) => {
                      const max = r.topics[0].count || 1;
                      return (
                        <div key={t.id} className="cx-qrow" title={t.decided ? `${t.asIs} of ${t.decided} drafts sent as written` : ''}>
                          <span>{t.label}</span>
                          <span className="cx-num">{t.count}</span>
                          <div className="cx-qbar"><i style={{ width: `${(t.count / max) * 100}%` }} />{t.decided > 0 && <em>{Math.round((t.asIs / t.decided) * 100)}% as written</em>}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
              <section className="cx-panel" aria-labelledby="cx-team-h">
                <div className="cx-panel-h"><h2 id="cx-team-h">Team workload</h2><span>assigned conversations</span></div>
                {r.people.length === 0 ? <div className="cx-empty" style={{ padding: 28 }}><strong>Nothing assigned</strong>Assign conversations in the inbox to see who is handling what.</div> : (
                  <table className="cx-table">
                    <thead><tr><th>Person</th><th className="r">Open now</th><th className="r">Resolved in period</th></tr></thead>
                    <tbody>{r.people.map((p) => <tr key={p.uid}><td>{p.name}</td><td className="r cx-num">{p.open}</td><td className="r cx-num">{p.resolved}</td></tr>)}</tbody>
                  </table>
                )}
              </section>
            </div>

            {r.carts && (
              <section className="cx-panel" style={{ marginTop: 20 }} aria-labelledby="cx-cart-h">
                <div className="cx-panel-h"><h2 id="cx-cart-h">Cart recovery</h2><span>carts left in this period</span></div>
                <div className="cx-metrics" style={{ border: 0, borderRadius: 0 }}>
                  <div className="cx-metric"><span>Carts left</span><b>{r.carts.total}</b><small>{inr(r.carts.value)} in carts</small></div>
                  <div className="cx-metric"><span>Followed up</span><b>{r.carts.total ? `${Math.round((r.carts.followedUp / r.carts.total) * 100)}%` : '—'}</b><small>{r.carts.followedUp} with a logged follow-up</small></div>
                  <div className="cx-metric"><span>Recovered</span><b>{r.carts.converted}</b><small>{r.carts.total ? `${Math.round((r.carts.converted / r.carts.total) * 1000) / 10}% of carts` : ''}</small></div>
                  <div className="cx-metric"><span>Recovered value</span><b>{inr(r.carts.convertedValue)}</b><small>marked converted</small></div>
                </div>
              </section>
            )}
            {r.aiReplyMin != null && <p className="cx-hint" style={{ marginTop: 16 }}>Median wait for replies sent from AI drafts: {dur(r.aiReplyMin)}.</p>}
          </div>
        )}
    </>
  );
  if (embedded) return body;
  return <div className="cx-page"><div className="cx-page-in">{body}</div></div>;
}

export default ReportsPage;
